import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
    Dialog,
    DialogContent,
    IconButton,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React from 'react';
import { getDiscoCompanyMenuItems } from '../AdminCompanySelect/DiscoCompanyMenuItems';
import { ADMIN_COMPANY_SELECT_OPTIONS } from '../../constants/discoCompanies';
import './SearchPopup.scss';

export const SEARCH_SERVICE_OPTIONS = [
    { id: 1, label: 'New Connection' },
    { id: 5, label: 'Meter Change Order' },
    { id: 7, label: 'Due Date Extension' },
    { id: 8, label: 'Bill Installments' },
    { id: 11, label: 'Meter Reading Correction' },
    { id: 13, label: 'Reconnection' },
    { id: 15, label: 'Change of Attributes' },
    { id: 16, label: 'Last Paid Bill Adjustment' },
    { id: 17, label: 'Part Payment' },
    { id: 18, label: 'Advance Payment' },
];

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        padding: '0px 14px',
    },
};

const noBrowserAutocomplete = {
    autoComplete: 'off',
    autoCorrect: 'off',
    autoCapitalize: 'off',
    spellCheck: 'false',
    readOnly: true,
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-form-type': 'other',
};

const unlockAutocomplete = (event) => {
    event.target.removeAttribute('readonly');
};

const SearchPopup = ({
    searchBarrPopupOpen,
    handleCloseSearchBarPopup,
    selectedTab,
    handleTabChange,
    tabLabels,
    searchFormik,
    isAdmin = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const tabCount = tabLabels.length;

    const goToPrevTab = () => {
        const next = selectedTab <= 0 ? tabCount - 1 : selectedTab - 1;
        handleTabChange(null, next);
    };

    const goToNextTab = () => {
        const next = selectedTab >= tabCount - 1 ? 0 : selectedTab + 1;
        handleTabChange(null, next);
    };

    const fieldError =
        (selectedTab === 0 && searchFormik.touched.iv_reference_no && searchFormik.errors.iv_reference_no) ||
        (selectedTab === 1 && searchFormik.touched.iv_application_id && searchFormik.errors.iv_application_id) ||
        (selectedTab === 2 && searchFormik.touched.iv_cnic && searchFormik.errors.iv_cnic) ||
        (selectedTab === 3 && searchFormik.touched.iv_applicant_name && searchFormik.errors.iv_applicant_name) ||
        '';
    const serviceError =
        searchFormik.touched.iv_application_type && searchFormik.errors.iv_application_type
            ? searchFormik.errors.iv_application_type
            : '';
    const companyError =
        isAdmin &&
        searchFormik.touched.iv_company_code &&
        searchFormik.errors.iv_company_code
            ? searchFormik.errors.iv_company_code
            : '';
    const hasService = Boolean(searchFormik.values.iv_application_type);
    const hasCompany = Boolean(searchFormik.values.iv_company_code);
    const canSubmit = hasService && (!isAdmin || hasCompany);

    const renderField = () => {
        if (selectedTab === 0) {
            return (
                <TextField
                    fullWidth
                    variant="outlined"
                    name="iv_reference_no"
                    id="iv_reference_no"
                    placeholder="Enter reference number"
                    value={searchFormik.values.iv_reference_no}
                    onChange={searchFormik.handleChange}
                    onBlur={searchFormik.handleBlur}
                    autoComplete="off"
                    onFocus={unlockAutocomplete}
                    inputProps={{ ...noBrowserAutocomplete, maxLength: 14 }}
                    sx={textFieldSx}
                />
            );
        }
        if (selectedTab === 1) {
            return (
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter application ID"
                    name="iv_application_id"
                    id="iv_application_id"
                    value={searchFormik.values.iv_application_id}
                    onChange={searchFormik.handleChange}
                    onBlur={searchFormik.handleBlur}
                    autoComplete="off"
                    onFocus={unlockAutocomplete}
                    inputProps={{ ...noBrowserAutocomplete, maxLength: 19 }}
                    sx={textFieldSx}
                />
            );
        }
        if (selectedTab === 2) {
            return (
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter CNIC"
                    name="iv_cnic"
                    id="iv_cnic"
                    value={searchFormik.values.iv_cnic}
                    onChange={searchFormik.handleChange}
                    onBlur={searchFormik.handleBlur}
                    autoComplete="off"
                    onFocus={unlockAutocomplete}
                    inputProps={{ ...noBrowserAutocomplete, maxLength: 13 }}
                    sx={textFieldSx}
                />
            );
        }
        return (
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter applicant name"
                name="iv_applicant_name"
                id="iv_applicant_name"
                value={searchFormik.values.iv_applicant_name}
                onChange={searchFormik.handleChange}
                onBlur={searchFormik.handleBlur}
                    autoComplete="off"
                    onFocus={unlockAutocomplete}
                    inputProps={{ ...noBrowserAutocomplete, minLength: 3 }}
                sx={textFieldSx}
            />
        );
    };

    return (
        <Dialog
            open={searchBarrPopupOpen}
            onClose={handleCloseSearchBarPopup}
            maxWidth={isMobile ? false : 'md'}
            fullWidth={isMobile}
            className={`search-popup${isMobile ? ' search-popup--mobile' : ''}${isAdmin ? ' search-popup--admin' : ''}`}
            sx={
                isMobile
                    ? {
                        '& .MuiDialog-paper': {
                            borderRadius: '12px',
                        },
                    }
                    : {
                        '& .MuiDialog-container': {
                            alignItems: 'flex-start',
                            justifyContent: 'flex-end',
                            padding: '72px 24px 24px',
                        },
                        '& .MuiDialog-paper': {
                            margin: 0,
                            borderRadius: '14px',
                            width: 'auto',
                            minWidth: `min(${isAdmin ? 760 : 620}px, calc(100vw - 48px))`,
                            maxWidth: `min(${isAdmin ? 820 : 680}px, calc(100vw - 48px))`,
                        },
                    }
            }
        >
            <DialogContent className="search-popup__content">
                <div className="search-popup__heading">
                    <span className="search-popup__heading-title">Search Application</span>
                    <span className="search-popup__heading-hint">
                        {isAdmin ? 'Please select a company and a service.' : 'Please select a service.'}
                    </span>
                </div>
                {isMobile ? (
                    <div className="search-popup__tab-nav" role="tablist" aria-label="Search type">
                        <IconButton
                            type="button"
                            className="search-popup__tab-nav-btn"
                            onClick={goToPrevTab}
                            aria-label="Previous search type"
                            size="small"
                        >
                            <ChevronLeftIcon fontSize="medium" />
                        </IconButton>
                        <button
                            type="button"
                            className="search-popup__tab-nav-label"
                            onClick={goToNextTab}
                            aria-label={`Current: ${tabLabels[selectedTab]}. Tap for next.`}
                        >
                            {tabLabels[selectedTab]}
                        </button>
                        <IconButton
                            type="button"
                            className="search-popup__tab-nav-btn"
                            onClick={goToNextTab}
                            aria-label="Next search type"
                            size="small"
                        >
                            <ChevronRightIcon fontSize="medium" />
                        </IconButton>
                    </div>
                ) : (
                    <div className="search-popup__tabs-wrap">
                        <Tabs
                            className="search-popup__tabs"
                            value={selectedTab}
                            onChange={handleTabChange}
                            textColor="inherit"
                            variant="standard"
                        >
                            {tabLabels.map((label, index) => (
                                <Tab key={index} label={label} />
                            ))}
                        </Tabs>
                    </div>
                )}
                <form
                    onSubmit={searchFormik.handleSubmit}
                    className="search-popup__form"
                    autoComplete="off"
                >
                    <input
                        type="text"
                        name="search_popup_autofill_trap"
                        autoComplete="off"
                        tabIndex={-1}
                        aria-hidden="true"
                        className="search-popup__autofill-trap"
                    />
                    <div className="search-popup__bar">
                        {isAdmin ? (
                            <div className={`search-popup__service search-popup__company${companyError ? ' is-error' : ''}${hasCompany ? '' : ' is-empty'}`}>
                                <CorporateFareOutlinedIcon className="search-popup__service-icon" />
                                <Select
                                    id="iv_company_code"
                                    name="iv_company_code"
                                    value={searchFormik.values.iv_company_code || ''}
                                    onChange={searchFormik.handleChange}
                                    onBlur={searchFormik.handleBlur}
                                    displayEmpty
                                    variant="standard"
                                    disableUnderline
                                    className="search-popup__service-select"
                                    renderValue={(selected) => {
                                        if (!selected) return 'Select company *';
                                        const match = ADMIN_COMPANY_SELECT_OPTIONS.find(
                                            (item) => String(item.value) === String(selected)
                                        );
                                        return match?.label || 'Select company *';
                                    }}
                                    MenuProps={{
                                        PaperProps: { className: 'search-popup__service-menu' },
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select company *</em>
                                    </MenuItem>
                                    {getDiscoCompanyMenuItems(
                                        searchFormik.values.iv_company_code,
                                        true,
                                        false
                                    )}
                                </Select>
                            </div>
                        ) : null}
                        <div className={`search-popup__service${serviceError ? ' is-error' : ''}${hasService ? '' : ' is-empty'}`}>
                            <AppsRoundedIcon className="search-popup__service-icon" />
                            <Select
                                id="iv_application_type"
                                name="iv_application_type"
                                value={searchFormik.values.iv_application_type}
                                onChange={searchFormik.handleChange}
                                onBlur={searchFormik.handleBlur}
                                displayEmpty
                                variant="standard"
                                disableUnderline
                                className="search-popup__service-select"
                                renderValue={(selected) => {
                                    if (!selected) return 'Select service *';
                                    const match = SEARCH_SERVICE_OPTIONS.find(
                                        (item) => String(item.id) === String(selected)
                                    );
                                    return match?.label || 'Select service *';
                                }}
                                MenuProps={{
                                    PaperProps: { className: 'search-popup__service-menu' },
                                }}
                            >
                                {SEARCH_SERVICE_OPTIONS.map((item) => (
                                    <MenuItem key={item.id} value={String(item.id)}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                        <div className="search-popup__field">{renderField()}</div>
                        <button
                            type="submit"
                            className="search-popup__submit-btn"
                            disabled={!canSubmit}
                            onClick={() => {
                                if (isAdmin && !hasCompany) {
                                    searchFormik.setFieldTouched('iv_company_code', true, true);
                                }
                                if (!hasService) {
                                    searchFormik.setFieldTouched('iv_application_type', true, true);
                                }
                            }}
                        >
                            <SearchRoundedIcon />
                            Go
                        </button>
                    </div>
                    {(companyError || serviceError || fieldError) && (
                        <p className="search-popup__error">{companyError || serviceError || fieldError}</p>
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default SearchPopup;
